import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin, AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";

const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();

// Get the connection string from environment variable (Vite automatically exposes VITE_ prefixed variables)
const connectionString = import.meta.env.AZURE_APPLICATIONINSIGHTS_CONNECTION_STRING;

let appInsights: ApplicationInsights | null = null;

if (connectionString) {
    appInsights = new ApplicationInsights({
        config: {
            connectionString: connectionString,
            extensions: [reactPlugin],
            extensionConfig: {
                [reactPlugin.identifier]: { history: browserHistory }
            },
            enableAutoRouteTracking: true,
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            autoTrackPageVisitTime: true,
            enableUnhandledPromiseRejectionTracking: true,
            disableFetchTracking: false
        }
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();
    console.debug("Application Insights initialized successfully with connection string");
} else {
    console.warn("Application Insights connection string not found. Client-side telemetry will not be collected.");
    console.debug("To enable Application Insights, set AZURE_APPLICATIONINSIGHTS_CONNECTION_STRING environment variable");
}

export { appInsights, reactPlugin, AppInsightsErrorBoundary };

// Helper functions for manual tracking
export const trackEvent = (name: string, properties?: Record<string, unknown>) => {
    if (appInsights) {
        appInsights.trackEvent({ name, properties });
    }
};

export const trackException = (exception: Error, properties?: Record<string, unknown>) => {
    if (appInsights) {
        appInsights.trackException({ exception, properties });
    }
};

export const trackTrace = (message: string, severityLevel?: number, properties?: Record<string, unknown>) => {
    if (appInsights) {
        appInsights.trackTrace({ message, severityLevel, properties });
    }
};

export const trackMetric = (name: string, average: number, properties?: Record<string, unknown>) => {
    if (appInsights) {
        appInsights.trackMetric({ name, average, properties });
    }
};
